/*
 * Marelle Cart Drawer (Pass 2 #4, Phase A + Phase B)
 *
 * Vanilla JS, single IIFE, no external dependencies. All logic
 * lives inside one init() body so the read, remove, and add
 * pipelines share scope; nothing is exposed on window.
 *
 * Phase A behavior (read + remove):
 *   - Open / close (click trigger, ESC, overlay click, close button).
 *   - Focus trap inside the drawer while open.
 *   - Body scroll lock with scrollbar-width reservation.
 *   - Remove-from-line: POST /cart/change.js, then GET /cart.js,
 *     then re-render body + foot + header badge from the JSON.
 *
 * Phase B behavior (add):
 *   - Capture-phase document submit listener catches any <form>
 *     whose action contains '/cart/add'. preventDefault +
 *     stopImmediatePropagation block Dawn's <product-form> bubble
 *     listener from running, so we own the request end-to-end.
 *     FormData carries variant id, quantity, line item properties,
 *     and gift card recipient fields unchanged.
 *   - Document click listener catches any element with
 *     [data-cart-add="<variant_id>"] (Featured Grid CTAs) and posts
 *     { id, quantity: 1 } to /cart/add.js.
 *   - On success: GET /cart.js, re-render drawer, open it. Drawer
 *     opening IS the confirmation - no toast.
 *   - On failure: console warn, brief shake on the triggering button
 *     (Featured Grid only; class is BEM-scoped to that section), and
 *     a polite aria-live announcement so SR users hear the error.
 *
 * Money format scope (Phase A, still applies):
 *   formatMoney(cents) supports exactly two shop.money_format
 *   placeholder tokens:
 *     {{amount}}              -> "$129.00"
 *     {{amount_no_decimals}}  -> "$129"
 *   Other tokens (amount_with_comma_separator, etc.) fall through
 *   to a console.warn and a sensible default: "$" + cents/100 with
 *   two decimals. Full token coverage is deferred until a non-US
 *   launch needs it.
 *
 * No-JS fallback:
 *   Dawn's product form remains a real <form action="/cart/add">.
 *   If JS fails to load, the browser submits normally and Shopify
 *   302s to /cart. The Featured Grid CTA degrades to a non-functional
 *   button without JS, but each card is still wrapped in a cover link
 *   to the PDP, so customers can still reach a working add-to-cart.
 */

(function () {
  function init() {
  var root = document.querySelector('[data-cart-drawer-root]');
  if (!root) return;

  var overlay = document.querySelector('[data-cart-drawer-overlay]');
  var body = root.querySelector('[data-cart-drawer-body]');
  var closeBtn = root.querySelector('[data-cart-drawer-close]');
  var subtotalEl = root.querySelector('[data-cart-drawer-subtotal]');
  var checkoutEl = root.querySelector('[data-cart-drawer-checkout]');
  var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
  var emptyImgSrc = root.getAttribute('data-empty-img-src') || '';
  var headerBadge = document.querySelector('[data-cart-count]');
  var triggers = document.querySelectorAll('[data-cart-drawer-open]');
  var announceEl = root.querySelector('[data-cart-drawer-announce]');

  var lastFocus = null;
  var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  var announceTimer = null;

  // ----- Open / close -------------------------------------------------

  function open(triggerEl) {
    lastFocus = triggerEl || document.activeElement;
    root.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('marelle-no-scroll');
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = scrollbarWidth + 'px';
    }
    document.addEventListener('keydown', onKeyDown);
    window.requestAnimationFrame(function () {
      if (closeBtn) closeBtn.focus();
    });
  }

  function close() {
    root.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('marelle-no-scroll');
    document.body.style.paddingRight = '';
    document.removeEventListener('keydown', onKeyDown);
    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus();
    }
  }

  // ----- Keyboard: ESC + focus trap -----------------------------------

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') {
      trapFocus(e);
    }
  }

  function trapFocus(e) {
    var focusables = root.querySelectorAll(
      'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
    );
    if (focusables.length === 0) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // ----- Trigger wiring -----------------------------------------------

  triggers.forEach(function (t) {
    t.addEventListener('click', function (e) {
      e.preventDefault();
      open(t);
    });
  });
  if (closeBtn) closeBtn.addEventListener('click', close);
  if (overlay) overlay.addEventListener('click', close);

  // ----- Remove (event-delegated on the drawer body) -------------------

  body.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cart-drawer-remove]');
    if (!btn) return;
    e.preventDefault();
    var key = btn.getAttribute('data-line-key');
    if (key) removeLine(key);
  });

  function removeLine(key) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
      .then(function () { return fetch('/cart.js'); })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderBody(cart);
        renderTotals(cart);
        renderHeaderBadge(cart);
      })
      .catch(function (err) {
        // Phase B: elevate Phase A's silent fail. Line stays in DOM
        // (user can retry by clicking Remove again), but we now
        // announce the failure so SR users hear it.
        console.warn('[marelle cart] remove failed', err);
        announce('Could not remove item from bag');
      });
  }

  // ----- Re-render after AJAX -----------------------------------------

  function renderBody(cart) {
    if (!cart.items || cart.items.length === 0) {
      body.innerHTML =
        '<div class="marelle-cart-drawer__empty">' +
          '<img src="' + escapeAttr(emptyImgSrc) + '" alt="" width="140" loading="lazy">' +
          '<p>Your bag is empty.<br>Set the table.</p>' +
        '</div>';
      return;
    }
    var html = '';
    cart.items.forEach(function (item) {
      html += lineHtml(item);
    });
    body.innerHTML = html;
  }

  function lineHtml(item) {
    var thumbHtml;
    if (item.image) {
      thumbHtml =
        '<div class="marelle-cart-drawer__thumb">' +
          '<img src="' + escapeAttr(item.image) + '" alt="' + escapeAttr(item.product_title) + '" loading="lazy">' +
        '</div>';
    } else {
      thumbHtml = '<div class="marelle-cart-drawer__thumb marelle-cart-drawer__thumb--empty"></div>';
    }
    var subHtml = '';
    if (item.variant_title && item.variant_title !== 'Default Title') {
      subHtml = '<div class="marelle-cart-drawer__sub">' + escapeText(item.variant_title) + '</div>';
    }
    return (
      '<div class="marelle-cart-drawer__line" data-line-key="' + escapeAttr(item.key) + '">' +
        thumbHtml +
        '<div class="marelle-cart-drawer__details">' +
          '<div class="marelle-cart-drawer__name">' + escapeText(item.product_title) + '</div>' +
          subHtml +
          '<button class="marelle-cart-drawer__remove" type="button" data-cart-drawer-remove data-line-key="' + escapeAttr(item.key) + '">Remove</button>' +
        '</div>' +
        '<div class="marelle-cart-drawer__price">' + formatMoney(item.final_line_price) + '</div>' +
      '</div>'
    );
  }

  function renderTotals(cart) {
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    if (!checkoutEl) return;
    if (cart.item_count === 0) {
      checkoutEl.setAttribute('aria-disabled', 'true');
      checkoutEl.setAttribute('tabindex', '-1');
    } else {
      checkoutEl.removeAttribute('aria-disabled');
      checkoutEl.removeAttribute('tabindex');
    }
  }

  function renderHeaderBadge(cart) {
    if (!headerBadge) return;
    headerBadge.textContent = cart.item_count;
    if (cart.item_count === 0) {
      headerBadge.setAttribute('hidden', '');
    } else {
      headerBadge.removeAttribute('hidden');
    }
    var link = headerBadge.closest('.marelle-header__cart');
    if (link) link.setAttribute('aria-label', 'Cart, ' + cart.item_count + ' items');
  }

  // ----- Add to cart (Phase B) ----------------------------------------
  //
  // Two entry points, one tail. Forms (Dawn product page) come in
  // through submitAddForm. data-cart-add elements (Featured Grid)
  // come in through addVariantId. Both pipe through refreshAndOpen,
  // which fetches /cart.js, re-renders, and opens the drawer.

  function submitAddForm(form, triggerEl) {
    var btn = triggerEl || form.querySelector('[type="submit"]');
    if (btn) btn.setAttribute('aria-disabled', 'true');
    var fd = new FormData(form);
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: fd
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed: ' + r.status);
        return r.json();
      })
      .then(function () { return refreshAndOpen(btn); })
      .catch(function (err) {
        console.warn('[marelle cart] add failed', err);
        if (btn) shake(btn);
        announce('Could not add item to bag');
      })
      .then(function () {
        if (btn) btn.removeAttribute('aria-disabled');
      });
  }

  function addVariantId(variantId, qty, triggerEl) {
    if (triggerEl) triggerEl.setAttribute('aria-disabled', 'true');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: variantId, quantity: qty || 1 })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed: ' + r.status);
        return r.json();
      })
      .then(function () { return refreshAndOpen(triggerEl); })
      .catch(function (err) {
        console.warn('[marelle cart] add failed', err);
        if (triggerEl) shake(triggerEl);
        announce('Could not add item to bag');
      })
      .then(function () {
        if (triggerEl) triggerEl.removeAttribute('aria-disabled');
      });
  }

  function refreshAndOpen(triggerEl) {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderBody(cart);
        renderTotals(cart);
        renderHeaderBadge(cart);
        open(triggerEl);
      });
  }

  // Capture-phase document submit listener. Capture phase + the
  // stopImmediatePropagation call below run before any bubble-phase
  // listener on the form, so Dawn's <product-form> submit handler
  // never sees the event. preventDefault stops the native submit.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    submitAddForm(form);
  }, true);

  // Click listener for [data-cart-add="<variant_id>"] elements
  // (Featured Grid CTAs). aria-disabled guard prevents double-clicks
  // during an in-flight request.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-cart-add]');
    if (!btn) return;
    if (btn.getAttribute('aria-disabled') === 'true') return;
    var variantId = btn.getAttribute('data-cart-add');
    if (!variantId) return;
    e.preventDefault();
    addVariantId(variantId, 1, btn);
  });

  // ----- Live region + shake (Phase B failure feedback) --------------

  function announce(msg) {
    if (!announceEl) return;
    announceEl.textContent = msg;
    if (announceTimer) clearTimeout(announceTimer);
    announceTimer = setTimeout(function () {
      announceEl.textContent = '';
      announceTimer = null;
    }, 3000);
  }

  // Explicit remove -> reflow -> add pattern so the keyframe re-fires
  // on rapid repeated failures (otherwise re-adding a class that's
  // already present is a no-op).
  function shake(el) {
    if (!el) return;
    el.classList.remove('marelle-featured-grid__cta--shake');
    void el.offsetWidth;
    el.classList.add('marelle-featured-grid__cta--shake');
    setTimeout(function () {
      el.classList.remove('marelle-featured-grid__cta--shake');
    }, 420);
  }

  // ----- Money formatting ---------------------------------------------

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    if (moneyFormat.indexOf('{{amount}}') !== -1) {
      return moneyFormat.replace('{{amount}}', (cents / 100).toFixed(2));
    }
    if (moneyFormat.indexOf('{{amount_no_decimals}}') !== -1) {
      return moneyFormat.replace('{{amount_no_decimals}}', String(Math.round(cents / 100)));
    }
    console.warn(
      '[marelle cart] unsupported money_format token in Phase A; using default. format=',
      moneyFormat
    );
    return '$' + (cents / 100).toFixed(2);
  }

  // ----- Helpers ------------------------------------------------------

  function escapeText(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function escapeAttr(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
  }

  // Defer attribute on the script tag SHOULD be enough on its own, but
  // we observed a case where document.querySelector('[data-cart-drawer-overlay]')
  // returned null at IIFE-body time even though root was found - a race we
  // could not reproduce statically. Gating init on DOMContentLoaded (or
  // running immediately if the DOM is already ready) guarantees both
  // elements exist before any query runs.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
